import { useEffect, useRef, type ReactNode } from "react";
import { animate, motion } from "motion/react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { CHIP_POP, FADE_ON_FRAMELOOP, withFade, type SpeechPose } from "./fade";

/** A state's dress on the speech bubble (Sebastian, 2026-10-21: "this
 *  could be a base for such enhancements for the chat bubble — we will use
 *  this kind of way for different states across the widget"); each tone is
 *  `.igw-speech[data-tone]` in `igw-button.css`, its pieces arriving and
 *  leaving with the bubble:
 *  - `hot`: a hot period — its edges glowing in heat, small flames burning
 *    along its top, catching one after another as it pops in. */
export type SpeechTone = "hot";

/** The bubble's exit: quick and critically damped — up a little, a touch
 *  smaller, gone in about a quarter second (Sebastian, 2026-10-21: "make the
 *  speech bubble exit a bit faster"; the fade spring took over a second to
 *  settle). */
const BUBBLE_OUT = {
  type: "spring",
  stiffness: 1100,
  damping: 66,
  mass: 1,
} as const;

/** The button's speech bubble (IGB-TW-07): the solid foreground badge as a
 *  bubble, tilted by its pose, popped in with one small overshoot and gone
 *  up a little, a touch smaller, faded. One for every moment in words — a
 *  tournament climb's "↑ N", a prize drop's "Hot right now!" and "€100 just
 *  dropped!" (Sebastian, 2026-10-21: "same speech bubble as we use in
 *  tournaments"). `pop` re-pops it in place when its text changes; the
 *  caller's `className` places it. `still` (reduced motion) shows it with
 *  no move. `tone` dresses it for a state (`SpeechTone`). */
export function SpeechBubble({
  pose,
  pop,
  still = false,
  tone,
  className,
  children,
}: {
  pose: SpeechPose;
  pop?: number | string;
  still?: boolean;
  tone?: SpeechTone;
  className?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    if (ref.current && !still)
      void animate(ref.current, { scale: [0.6, 1] }, CHIP_POP);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pop]);
  return (
    <motion.span
      ref={ref}
      className={cn("igw-speech", className)}
      data-tone={tone}
      data-still={still || undefined}
      aria-hidden="true"
      style={{ x: pose.x, rotate: pose.rotate, marginTop: pose.y }}
      initial={still ? false : { scale: 0.4, opacity: 0, y: 0 }}
      animate={{ scale: 1, opacity: 1, y: 0, transition: withFade(CHIP_POP) }}
      onUpdate={FADE_ON_FRAMELOOP}
      exit={
        still
          ? undefined
          : { opacity: 0, y: -8, scale: 0.9, transition: BUBBLE_OUT }
      }
    >
      <Badge variant="solid" color="foreground" shape="bubble" size="sm">
        {children}
      </Badge>
      {tone === "hot" && (
        <span className="igw-speech__fx">
          <i className="igw-speech__flame" />
          <i className="igw-speech__flame" />
          <i className="igw-speech__flame" />
        </span>
      )}
    </motion.span>
  );
}
